import { createContext, useContext, useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash.includes("session_id=")) {
        const sessionId = hash.split("session_id=")[1].split("&")[0];
        await processSessionId(sessionId);
        // Clean URL
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }

      // Check existing session
      const response = await axiosInstance.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.log("No valid session");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await axiosInstance.post(
        "/auth/session",
        {},
        {
          headers: {
            "X-Session-ID": sessionId,
          },
        }
      );
      setUser(response.data.user);
      toast.success("Successfully logged in!");
    } catch (error) {
      console.error("Session processing error:", error);
      if (error.response?.status === 403) {
        toast.error("Only @emergent.sh emails are allowed");
      } else {
        toast.error("Authentication failed");
      }
      setUser(null);
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
